import AirplanemodeActiveIcon from "@mui/icons-material/AirplanemodeActive";
import FlightIcon from "@mui/icons-material/Flight";
import InfoIcon from "@mui/icons-material/Info";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../../components/EmptyState";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useRampsState } from "../../containers/RampsStateContainer";
import { Ramp } from "../../types";
import { scrollSx } from "../../utils";
import { sum } from "../../utils/lodash-replacements";
import { RampIcon } from "../../widgets/Icons/RampIcon";
import { StatCard } from "../Hangars/components/StatCard";
import { sortByNumberThenText } from "../Hangars/HangarsPresenter";
import { RampRow } from "./components/RampRow";
import { RampsSkeleton } from "./RampsSkeleton";

export const sortRamps = (
  a: Ramp,
  b: Ramp,
  rampDisplayOrder?: string[]
): number => {
  if (rampDisplayOrder) {
    const aIndex = rampDisplayOrder.indexOf(a.id);
    const bIndex = rampDisplayOrder.indexOf(b.id);
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    } else if (aIndex !== -1) {
      return -1;
    } else if (bIndex !== -1) {
      return 1;
    }
  }
  return sortByNumberThenText(a.name, b.name);
};

type Props = {};

export const RampsPresenter: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();
  const { ramps, ready } = useRampsState();

  const sortedRamps = [...ramps].sort((a, b) =>
    sortRamps(a, b, activeFBO?.settings?.rampDisplayOrder)
  );

  return (
    <Box
      sx={{
        ml: 2,
        mr: 2,
        height: "100%",
        minHeight: "calc(100vh - 64px)",
        pb: 4,
      }}
    >
      {!ready ? (
        <RampsSkeleton />
      ) : (
        <Stack
          direction="column"
          sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              mt: 2,
              mb: 2.25,
              pb: 1.5,
              borderBottom: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            <Typography variant="h6">{t('screens.ramps.rampsPresenter.title', { fboName: activeFBO?.name })}</Typography>
          </Stack>

          {/* Stat Cards */}
          <Stack
            direction="row"
            spacing={2}
            sx={{
              mb: 2,
              width: "100%",
              maxWidth: 900,
              "& > *": {
                flex: 1,
                maxWidth: 420,
              },
            }}
          >
            <StatCard
              icon={RampIcon}
              label={t('screens.ramps.rampsPresenter.stats.totalRamps')}
              value={ramps.length}
              testId="total_ramps"
            />
            <StatCard
              icon={FlightIcon}
              label={t('screens.ramps.rampsPresenter.stats.totalAircraft')}
              value={sum(ramps.map((r) => r.stack?.tenants?.length ?? 0))}
              testId="total_aircraft"
            />
          </Stack>

          {/* Ramp List */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              minHeight: 0,
              bgcolor: "background.paper",
              ...scrollSx,
            }}
          >
            {/* Header Row */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "200px 160px 1fr",
                alignItems: "center",
                gap: 2,
                px: 2.5,
                py: 1,
                borderBottom: "1px solid",
                borderColor: "grey.200",
                position: "sticky",
                top: 0,
                zIndex: 1,
                bgcolor: "background.paper",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500, fontSize: "0.95rem" }}
              >
                {t('screens.ramps.rampsPresenter.columns.rampName')}
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                justifyContent="flex-end"
              >
                <AirplanemodeActiveIcon
                  sx={{
                    color: "text.disabled",
                    fontSize: "0.85rem",
                    opacity: 0.8,
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 500, fontSize: "0.95rem" }}
                >
                  {t('screens.ramps.rampsPresenter.columns.currentAircraft')}
                </Typography>
                <Tooltip title={t('screens.ramps.rampsPresenter.tooltips.aircraftCount')}>
                  <InfoIcon
                    sx={{
                      color: "text.disabled",
                      fontSize: "0.85rem",
                      opacity: 0.8,
                    }}
                  />
                </Tooltip>
              </Stack>
              <Stack direction="row" justifyContent="flex-end">
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 500, fontSize: "0.95rem" }}
                >
                  {t('screens.ramps.rampsPresenter.columns.actions')}
                </Typography>
              </Stack>
            </Box>

            {/* Ramp Rows */}
            {sortedRamps.length === 0 ? (
              <EmptyState
                title={t('screens.ramps.rampsPresenter.emptyState.title')}
                description={t('screens.ramps.rampsPresenter.emptyState.description')}
                icon={<RampIcon sx={{ fontSize: 64, opacity: 0.5 }} />}
              />
            ) : (
              sortedRamps.map((ramp) => (
                <RampRow
                  key={ramp.id}
                  ramp={ramp}
                  aircraftCount={ramp.stack?.tenants?.length ?? 0}
                  enableScheduling={
                    activeFBO?.settings?.enableScheduling ?? false
                  }
                />
              ))
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
};
